import React, { useEffect, useRef, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { sharedApi } from "@/lib/api";

/**
 * Home hero carousel — Iter 654 legacy parity.
 *
 * Founder: "why the banner is not rotated… also get the banner from the
 * legacy website, same as it is". The backend now serves the legacy home
 * creatives (1920×400, text baked into the artwork) from
 * GET /api/classifieds/banners?country=NZ&placement=home_hero, sorted by
 * priority desc. We render them EXACTLY like the legacy Bootstrap slider:
 *  - every banner in order (no overlays, no local fallbacks, no signup slide
 *    — the prio-30 "FREE Registration & Listing" creative covers that pitch)
 *  - fixed 24:5 aspect on every breakpoint (creatives are designed for it)
 *  - auto-rotate 5 s, paused on hover/touch; swipe on touch; arrows md+
 *  - dot indicators, active dot elongated in brand orange #FF6200
 *  - external cta_href opens a new tab (noopener); relative routes in-app
 *  - first image preloaded (fetchPriority high), the rest lazy
 *  - empty / error → render nothing (no skeleton gap)
 */

const ROTATE_MS = 5000;
const SWIPE_PX = 40;

function useHomeHeroBanners() {
  return useQuery({
    queryKey: ["banners", "home_hero"],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/banners", {
        params: { country: "NZ", placement: "home_hero" },
      });
      // Iter 665 §4 (defensive): drop non-image_only slides too square to
      // ever fill a 24:5 hero (aspect_ratio < 2) — e.g. 1:1 stock photos.
      return (data.banners || []).filter(
        (b) => !(b.image_only === false && typeof b.aspect_ratio === "number" && b.aspect_ratio < 2)
      );
    },
    staleTime: 5 * 60_000,
    retry: 0,
  });
}

export default function HeroCarousel() {
  const nav = useNavigate();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef(null);
  const { data: banners = [], isLoading, isError } = useHomeHeroBanners();

  const total = banners.length;
  const next = useCallback(() => setIdx((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + total) % total), [total]);

  useEffect(() => {
    setIdx(0);
  }, [total]);

  useEffect(() => {
    if (total < 2 || paused) return undefined;
    const t = setInterval(next, ROTATE_MS);
    return () => clearInterval(t);
  }, [next, total, paused]);

  const onClickBanner = (b) => (e) => {
    window.posthog?.capture?.("hero_banner_click", { id: b.id, headline: b.headline });
    const href = b.cta_href || "";
    const isExternal = href.startsWith("http") || href.startsWith("mailto");
    if (!href) {
      e.preventDefault();
      return;
    }
    if (!isExternal) {
      e.preventDefault();
      nav(href);
    }
    // External: let the anchor's target="_blank" do its thing.
  };

  const onTouchStart = (e) => {
    touchX.current = e.touches?.[0]?.clientX ?? null;
    setPaused(true);
  };
  const onTouchEnd = (e) => {
    const start = touchX.current;
    touchX.current = null;
    setPaused(false);
    if (start == null || total < 2) return;
    const end = e.changedTouches?.[0]?.clientX;
    if (end == null) return;
    const delta = end - start;
    if (delta <= -SWIPE_PX) next();
    else if (delta >= SWIPE_PX) prev();
  };

  // Loading keeps the real footprint so the page doesn't jump; empty or
  // error renders NOTHING per the work order (no skeleton gap).
  if (isLoading) {
    return (
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-4 md:pt-6" data-testid="hero-carousel" data-hero-source="loading">
        <div className="w-full aspect-[24/5] rounded-2xl bg-slate-100 animate-pulse" />
      </section>
    );
  }
  if (isError || total === 0) return null;

  return (
    <section
      className="max-w-7xl mx-auto px-4 md:px-8 pt-4 md:pt-6"
      data-testid="hero-carousel"
      data-hero-source="api"
    >
      <div
        className="relative w-full overflow-hidden rounded-2xl shadow-card group"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="grid grid-cols-[minmax(0,1fr)] w-full">
          {banners.map((b, i) => {
            const active = i === idx;
            const href = b.cta_href || "";
            const isExternal = href.startsWith("http") || href.startsWith("mailto");
            return (
              <a
                key={b.id || i}
                href={href || undefined}
                target={isExternal ? "_blank" : undefined}
                rel={isExternal ? "noopener noreferrer" : undefined}
                onClick={onClickBanner(b)}
                draggable={false}
                className={`relative col-start-1 row-start-1 w-full aspect-[24/5] transition-opacity duration-700 ease-out ${
                  active ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
                }`}
                aria-hidden={!active}
                data-testid={`hero-slide-${i}`}
              >
                {/* Iter 665: creatives that aren't 24:5 are letterboxed
                    (object-contain) over a blurred copy of themselves —
                    never cropped or stretched. 24:5 creatives cover exactly. */}
                {b.fits_24_5 === false && (
                  <img
                    src={b.image_url}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover select-none blur-2xl scale-110 bg-[#F3F4F6]"
                    loading={i === 0 ? "eager" : "lazy"}
                    draggable={false}
                  />
                )}
                <img
                  src={b.image_url}
                  alt={b.headline || ""}
                  className={`relative w-full h-full select-none ${
                    b.fits_24_5 === false ? "object-contain" : "object-cover"
                  }`}
                  loading={i === 0 ? "eager" : "lazy"}
                  fetchPriority={i === 0 ? "high" : undefined}
                  draggable={false}
                />
              </a>
            );
          })}
        </div>

        {total > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              aria-label="Previous banner"
              data-testid="hero-carousel-prev"
              className="hidden md:grid absolute z-20 left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white text-slate-800 place-items-center shadow-card transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next banner"
              data-testid="hero-carousel-next"
              className="hidden md:grid absolute z-20 right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white text-slate-800 place-items-center shadow-card transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <div
              className="absolute z-20 left-1/2 -translate-x-1/2 bottom-2 md:bottom-4 flex items-center gap-1.5"
              data-testid="hero-carousel-dots"
            >
              {banners.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIdx(i)}
                  aria-label={`Go to banner ${i + 1}`}
                  aria-current={i === idx}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx ? "w-6 bg-[#FF6200]" : "w-1.5 bg-white/70 hover:bg-white"
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
